"use client";
import { FundamentalMenuItems, SentimentMenuItems, TechnicalMenuItems } from "@/app/components/BtcPanelSideMenu";
import ProTag from "@/app/components/ProTag";
import CheckIcon from "@mui/icons-material/Check";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Drawer, IconButton, Link, Popover, Tab, Tabs, Typography } from "@mui/material";
import { Funnel } from "lucide-react";
import { useTranslations } from "next-intl";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { useState, useRef, useEffect } from "react";
import { useTheme } from "@mui/material/styles";

const tabKeys = ["btc", "eth", "sol"];

export default function AnalyticsTabs() {
  const t = useTranslations("Analytics");
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [filterOpen, setFilterOpen] = React.useState(false);
  const theme = useTheme();
  const tabsRef = useRef<HTMLDivElement>(null);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);

  const tabParam = searchParams?.get("tab") || null;
  // 判断当前路径是否为投资组合页面
  const isPortfolioPage = pathname?.includes("/products/portfolio");
  const tabIndex = isPortfolioPage ? 3 : tabParam === "eth" ? 1 : tabParam === "sol" ? 2 : 0;

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    // 如果点击的是tab4 (index 3)，跳转到投资组合页面
    if (newValue === 3) {
      router.push("/products/portfolio");
      return;
    }

    const params = new URLSearchParams(searchParams?.toString() || "");
    if (newValue === 0) params.delete("tab");
    else params.set("tab", tabKeys[newValue]);
    router.replace(`/products/analytics${params.toString() ? "?" + params.toString() : ""}`);
  };

  const handleFilterOpen = () => {
    setFilterOpen(true);
  };

  const handleFilterClose = () => {
    setFilterOpen(false);
  };

  useEffect(() => {
    // MUI Tabs 的滚动容器是 .MuiTabs-scroller
    function getScroller(): HTMLElement | null {
      if (!tabsRef.current) return null;
      return tabsRef.current.querySelector(".MuiTabs-scroller");
    }
    function checkShadow() {
      const el = getScroller();
      if (!el) return;
      const isOverflowing = el.scrollWidth > el.clientWidth + 1;
      setShowLeftShadow(isOverflowing && el.scrollLeft > 2);
      setShowRightShadow(isOverflowing && el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
    }
    const scroller = getScroller();
    if (!scroller) return;
    checkShadow();
    scroller.addEventListener("scroll", checkShadow);
    window.addEventListener("resize", checkShadow);
    const observer = new MutationObserver(checkShadow);
    observer.observe(scroller, { childList: true, subtree: true });
    setTimeout(checkShadow, 100);
    return () => {
      scroller.removeEventListener("scroll", checkShadow);
      window.removeEventListener("resize", checkShadow);
      observer.disconnect();
    };
  }, []);

  return (
    <Box
      sx={{
        position: "relative",
        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
      }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          pl: 3,
          position: "relative",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
        }}>
        {/* 左侧阴影 */}
        {showLeftShadow && (
          <Box
            sx={{
              display: "block",
              position: "absolute",
              left: "1.5rem",
              top: 0,
              width: "2.25rem",
              height: "100%",
              pointerEvents: "none",
              zIndex: 2,
              background:
                theme.palette.mode === "dark"
                  ? "linear-gradient(90deg, #161A1E 0%, rgba(22,26,30,0.00) 100%)"
                  : "linear-gradient(90deg, #FFFAF5 0%, rgba(255,255,255,0.00) 100%)",
            }}
          />
        )}
        {/* 右侧阴影 */}
        {showRightShadow && (
          <Box
            sx={{
              display: "block",
              position: "absolute",
              // 如果是btc tab，右侧有funnel按钮，宽度加上按钮宽度
              right: tabIndex === 0 ? "2.25rem" : 0,
              top: 0,
              width: "2.25rem",
              height: "100%",
              pointerEvents: "none",
              zIndex: 2,
              background:
                theme.palette.mode === "dark"
                  ? "linear-gradient(270deg, #161A1E 0%, rgba(22,26,30,0.00) 100%)"
                  : "linear-gradient(270deg, #FFFAF5 0%, rgba(255,255,255,0.00) 100%)",
            }}
          />
        )}
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          sx={{
            flex: 1,
            minWidth: 0,
            overflowX: "auto",
          }}
          variant="scrollable"
          ref={tabsRef}>
          <Tab
            label={t("tab1.label")}
            sx={{
              px: 0,
              py: 2,
              m: 0,
              mr: 3.5,
              fontWeight: 600,
              minWidth: 0,
              minHeight: 0,
              lineHeight: "auto",
              textTransform: "none",
              color: "text.primary",
              "&.Mui-selected": {
                color: "text.primary",
              },
            }}
          />
          <Tab
            label={t("tab2.label")}
            sx={{
              px: 0,
              py: 2,
              m: 0,
              mr: 3.5,
              fontWeight: 600,
              minWidth: 0,
              minHeight: 0,
              lineHeight: "auto",
              textTransform: "none",
              color: "text.primary",
              "&.Mui-selected": {
                color: "text.primary",
              },
            }}
          />
          <Tab
            label={t("tab3.label")}
            sx={{
              px: 0,
              py: 2,
              m: 0,
              mr: 3.5,
              fontWeight: 600,
              minWidth: 0,
              minHeight: 0,
              lineHeight: "auto",
              textTransform: "none",
              color: "text.primary",
              "&.Mui-selected": {
                color: "text.primary",
              },
            }}
          />
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <ProTag text="Prime" />
                {t("tab4.label")}
              </Box>
            }
            sx={{
              px: 0,
              py: 2,
              m: 0,
              mr: 3.5,
              fontWeight: 600,
              minWidth: 0,
              minHeight: 0,
              lineHeight: "auto",
              textTransform: "none",
              color: "text.primary",
              "&.Mui-selected": {
                color: "text.primary",
              },
            }}
          />
        </Tabs>
        {/* 过滤图标 - 仅在 md 以下显示 */}
        {tabIndex === 0 && (
          <IconButton
            onClick={handleFilterOpen}
            sx={{
              display: { xs: "flex", md: "none" },
              ml: 1,
              color: "text.secondary",
            }}>
            <Funnel width="1.25rem" height="1.25rem" />
          </IconButton>
        )}
      </Box>

      {/* 底部菜单 */}
      <Drawer
        anchor="bottom"
        open={filterOpen}
        onClose={handleFilterClose}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            maxHeight: "80vh",
            padding: 2,
            backgroundColor: "background.paperMedium",
          },
        }}>
        <Box>
          <BtcPanelSideMenuContent />
        </Box>
      </Drawer>
    </Box>
  );
}

// 从 BtcPanelSideMenu 提取的内容组件，用于在底部菜单中显示
function BtcPanelSideMenuContent() {
  const t = useTranslations();
  const pathname = usePathname();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [popoverType, setPopoverType] = useState<"technical" | "fundamental" | "sentimental" | null>(null);

  const handleInfoClick =
    (type: "technical" | "fundamental" | "sentimental") => (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
      setPopoverType(type);
    };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setPopoverType(null);
  };

  const open = Boolean(anchorEl);

  // 判断当前url是否为菜单项
  const isCurrent = (url: string) => pathname === url;

  return (
    <Box sx={{ pt: "1.375rem", pb: 4, px: 1 }}>
      <Typography variant="h6" sx={{ fontSize: "var(--font-size-s)", mb: 5.75 }}>
        {t("Analytics.sideMenu.investingSignal")}
      </Typography>
      {/* 技术指标 */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", pb: 1 }}>
          <Typography sx={{ fontSize: "var(--font-size-xs)", fontWeight: 600, color: "text.secondary" }}>
            {t("Analytics.sideMenu.technical")}
          </Typography>
          <IconButton size="small" sx={{ ml: 1, color: "text.secondary" }} onClick={handleInfoClick("technical")}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
        {TechnicalMenuItems.map((item, idx) => {
          const selected = isCurrent(item.url);
          return IndicatorMenuItem(idx, selected, item, t);
        })}
      </Box>
      {/* 基本面指标 */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", pb: 1 }}>
          <Typography sx={{ fontSize: "var(--font-size-xs)", fontWeight: 600, color: "text.secondary" }}>
            {t("Analytics.sideMenu.fundamental")}
          </Typography>
          <IconButton size="small" sx={{ ml: 1, color: "text.secondary" }} onClick={handleInfoClick("fundamental")}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
        {FundamentalMenuItems.map((item, idx) => {
          const selected = isCurrent(item.url);
          return IndicatorMenuItem(idx, selected, item, t);
        })}
      </Box>
      {/* 情绪指标 */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", pb: 1 }}>
          <Typography sx={{ fontSize: "var(--font-size-xs)", fontWeight: 600, color: "text.secondary" }}>
            {t("Analytics.sideMenu.sentimental")}
          </Typography>
          <IconButton size="small" sx={{ ml: 1, color: "text.secondary" }} onClick={handleInfoClick("sentimental")}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
        {SentimentMenuItems.map((item, idx) => {
          const selected = isCurrent(item.url);
          return IndicatorMenuItem(idx, selected, item, t);
        })}
      </Box>
      {/* 信息 Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={{ pointerEvents: "auto" }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              maxWidth: 260,
              fontSize: "var(--font-size-2xs)",
              borderRadius: "0.5rem",
              boxShadow: 3,
              bgcolor: "background.paperExtreme",
            },
          },
        }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {popoverType === "technical" && t("Analytics.sideMenu.technicalInfo")}
          {popoverType === "fundamental" && t("Analytics.sideMenu.fundamentalInfo")}
          {popoverType === "sentimental" && t("Analytics.sideMenu.sentimentalInfo")}
        </Typography>
      </Popover>
    </Box>
  );
}
function IndicatorMenuItem(
  idx: number,
  selected: boolean,
  item: { label: string; url: string, isPro?: boolean },
  t: any
): React.JSX.Element {
  return (
    <Box
      key={idx}
      sx={{
        display: "flex",
        alignItems: "center",
        bgcolor: selected ? "background.paper" : "transparent",
        borderRadius: selected ? 2 : 0,
        transition: "background 0.2s",
        py: 2,
        px: 1.5,
        mx: -1.5,
        my: 0.75,
      }}>
      {selected && <CheckIcon sx={{ color: "text.primary", fontSize: "1.5rem", ml: 0.5, mr: 1 }} />}
      <Link
        href={item.url}
        sx={{
          color: "text.primary",
          fontWeight: 600,
          textDecoration: "none",
          display: "block",
          flex: 1,
        }}>
        {item.isPro && <ProTag text="Prime" sx={{ display: "inline", mr: 1, position: "relative", top: "-2px" }} />}
        {t(item.label as any)}
      </Link>
    </Box>
  );
}
